// Lead Nurture assistant (routes/nurture_agent.py): FPA answers leads' WhatsApp / Instagram / Facebook messages as
// "the LIFTR team" and gets a time for a call. Managers: the Assistant tab. Everyone: the card line and the GHL bot warning.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getNurtureAgent, getNurtureAgentAB, saveNurtureAgentSettings, nurtureAgentTakeOver, nurtureAgentEnable, nurtureAgentCheckBots, nurtureAgentTakeOverLead,
} from "@/lib/api";

const err = (e, fallback) => e?.response?.data?.detail || fallback;
const shortGym = (name) => (name || "").split(" - ").pop();
const CHANNEL = { WhatsApp: "WhatsApp", IG: "Instagram", FB: "Facebook" };
const INTENT = {
  chat: "Chat", call_time_given: "Call time", wants_call_now: "Call now", not_interested: "Not interested",
  wrong_person: "Wrong person", existing_member: "Member", price_question: "Price", human_requested: "Wants a person",
  complaint: "Complaint", unclear: "Unclear",
};
const ACTION = {
  replied: "Replied", callback_set: "Callback set", callback_no_lead: "Time given (no lead)", call_now: "Due now on the board",
  call_now_no_lead: "Call now (no lead)", handover: "Needs a person", not_interested: "Closed politely", stopped: "Stopped",
};
const MODE = {
  on: { label: "On: replies are sent", cls: "border-emerald-500/40 text-emerald-300" },
  dry: { label: "Dry run: replies are written and logged, nothing is sent", cls: "border-amber-500/40 text-amber-300" },
  off: { label: "Off", cls: "border-zinc-600 text-zinc-400" },
};

function ago(iso) {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return new Date(iso).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

// "GHL Conversation AI still on in <gym>": leads would get two replies.
export function GhlBotWarning({ warnings }) {
  if (!warnings?.length) return null;
  return (
    <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.07] px-4 py-2.5 text-sm text-rose-200 space-y-0.5" data-testid="ghl-bot-warning">
      {warnings.map((w) => (
        <div key={w.facility_id}>
          {w.message}{w.agents?.length ? ` (${w.agents.join(", ")})` : ""}.
        </div>
      ))}
      <div className="text-[11px] text-rose-300/80">GoHighLevel → AI Agents → Conversation AI: set the bot to Off for WhatsApp, Instagram and Facebook.</div>
    </div>
  );
}

// The card: "Assistant replied 2m ago" / "Needs a person" / "Wants a call now".
export function AgentLine({ lead, onChanged }) {
  const a = lead.agent;
  const [busy, setBusy] = useState(false);
  if (!a || !a.at || Date.now() - new Date(a.at).getTime() > 3 * 86400000) return null;
  const ch = CHANNEL[a.channel] || a.channel || "chat";
  const stop = async () => {
    setBusy(true);
    try {
      await nurtureAgentTakeOverLead(lead.id);
      toast.success("The assistant won't reply to this lead");
      onChanged?.();
    } catch (e) {
      toast.error(err(e, "Couldn't stop the assistant"));
    } finally {
      setBusy(false);
    }
  };
  const base = "mt-1 text-[11px] flex flex-wrap items-center gap-1";
  if (a.state === "needs_person") {
    return (
      <div className={`${base} rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-2 py-1 text-amber-200`} data-testid={`agent-${lead.id}`}>
        <Bot className="h-3 w-3" /> Needs a person (assistant stopped) on {ch}{a.reason ? `: ${a.reason}` : ""}
      </div>
    );
  }
  if (a.state === "call_now") {
    return <div className={`${base} text-rose-300`} data-testid={`agent-${lead.id}`}><Bot className="h-3 w-3" /> Wants a call now ({ch}) · {ago(a.at)}</div>;
  }
  if (a.state === "not_interested" || a.state === "wrong_person") {
    return (
      <div className={`${base} text-amber-300/90`} data-testid={`agent-${lead.id}`}>
        <Bot className="h-3 w-3" /> {a.state === "wrong_person" ? "Wrong person" : "Not interested"} on {ch} · suggested outcome: {a.state === "wrong_person" ? "Wrong number" : "Not interested"}
      </div>
    );
  }
  if (a.state === "taken_over") {
    return <div className={`${base} text-zinc-500`} data-testid={`agent-${lead.id}`}><Bot className="h-3 w-3" /> Assistant off for this lead ({a.reason || "taken over"})</div>;
  }
  return (
    <div className={`${base} text-violet-300/90`} data-testid={`agent-${lead.id}`}>
      <Bot className="h-3 w-3" /> Assistant replied {ago(a.last_reply_at || a.at)} on {ch}{a.callback_label ? ` · call ${a.callback_label}` : ""}
      <button onClick={stop} disabled={busy} className="ml-1 underline text-zinc-400 hover:text-zinc-200" data-testid={`agent-stop-${lead.id}`}>Take over</button>
    </div>
  );
}

// A/B test: the assistant books the consult ("book") vs gets a time for a call from the team ("call").
const pct = (a, b) => (b ? `${Math.round((100 * a) / b)}%` : "–");
function ABCard() {
  const [ab, setAb] = useState(null);
  useEffect(() => { getNurtureAgentAB().then(setAb).catch(() => setAb(null)); }, []);
  if (!ab) return null;
  const rows = [["book", "Assistant books the consult"], ["call", "Assistant gets a call time, the team books"]];
  const all = ab.all_calls_pickup || {};
  return (
    <div className="card-surface p-4 space-y-2" data-testid="assistant-ab">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">A/B test, last {ab.days} days</h3>
        <span className={`text-xs ${ab.on ? "text-emerald-300" : "text-zinc-500"}`}>{ab.on ? "Running (50/50 per lead)" : "Off"}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500 text-left">
            <tr><th className="py-1 pr-3">Arm</th><th className="pr-3">Leads</th><th className="pr-3">Booked</th><th className="pr-3">Showed</th><th className="pr-3">Picked up at their time</th></tr>
          </thead>
          <tbody>
            {rows.map(([k, label]) => {
              const r = ab.arms[k] || {};
              return (
                <tr key={k} className="border-t border-[#1f1f1f]">
                  <td className="py-1.5 pr-3">{label}</td>
                  <td className="pr-3 tabular-nums">{r.leads}</td>
                  <td className="pr-3 tabular-nums">{r.booked} <span className="text-zinc-500">({pct(r.booked, r.leads)})</span></td>
                  <td className="pr-3 tabular-nums">{r.showed} <span className="text-zinc-500">({pct(r.showed, r.leads)})</span></td>
                  <td className="pr-3 tabular-nums">{k === "call" ? <>{r.picked_up}/{r.callbacks_rung} <span className="text-zinc-500">({pct(r.picked_up, r.callbacks_rung)})</span></> : "–"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-500">All calls in this period: {pct(all.picked_up, all.calls)} picked up. Assistant bookings get confirmation calls 24 hours and 4 to 1 hours before.</p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-[#222] px-3 py-2">
      <div className="text-lg font-semibold">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    </div>
  );
}

export function AssistantTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => getNurtureAgent().then(setData).catch((e) => toast.error(err(e, "Couldn't load the assistant"))), []);
  useEffect(() => {
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), 30000);
    return () => clearInterval(t);
  }, [load]);
  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;
  const mode = MODE[data.mode] || MODE.dry;
  const off = new Set(data.settings.gyms_off || []);
  const save = async (settings) => {
    setBusy(true);
    try {
      await saveNurtureAgentSettings(settings);
      await load();
    } catch (e) {
      toast.error(err(e, "Couldn't save"));
    } finally {
      setBusy(false);
    }
  };
  const toggleGym = (fid) => save({ ...data.settings, gyms_off: off.has(fid) ? data.settings.gyms_off.filter((g) => g !== fid) : [...data.settings.gyms_off, fid] });
  const convAction = async (fn, id, msg) => {
    try {
      await fn(id);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(err(e, "Couldn't change it"));
    }
  };
  const checkBots = async () => {
    setBusy(true);
    try {
      await nurtureAgentCheckBots();
      await load();
      toast.success("Checked GoHighLevel's Conversation AI in every gym");
    } catch (e) {
      toast.error(err(e, "Couldn't check"));
    } finally {
      setBusy(false);
    }
  };
  const t = data.totals || {};
  const handovers = data.conversations.filter((c) => c.state === "handover");
  const others = data.conversations.filter((c) => c.state !== "handover");
  return (
    <div className="space-y-4" data-testid="assistant-tab">
      <GhlBotWarning warnings={data.bot_warnings} />
      <div className="card-surface p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold flex items-center gap-1.5"><Bot className="h-4 w-4" /> LIFTR assistant</h3>
            <p className="text-[11px] text-zinc-500 max-w-2xl">
              Answers new leads on WhatsApp, Instagram and Facebook as the LIFTR team, 24/7, and asks when the team can call. It never quotes prices,
              never books and never sends booking links; it says it&apos;s an assistant if asked. It doesn&apos;t answer members, contacts on DND, or
              conversations someone in the team wrote in during the last {data.limits.human_quiet_hours} h. At most {data.limits.cap_24h} messages per
              conversation a day, then a person takes over. Model: {data.model}.
            </p>
          </div>
          <span className={`rounded-full border px-2.5 py-0.5 text-[11px] ${mode.cls}`} data-testid="assistant-mode">{mode.label}</span>
        </div>
        <div className="text-[11px] text-zinc-500">Mode is set on Railway (NURTURE_AGENT = on / dry / off). Per gym:</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {data.gyms.map((g) => (
            <label key={g.facility_id} className="flex items-center gap-1.5 text-xs text-zinc-300">
              <input type="checkbox" checked={!off.has(g.facility_id)} disabled={busy} onChange={() => toggleGym(g.facility_id)} data-testid={`assistant-gym-${g.facility_id}`} />
              {shortGym(g.gym)}
              {!g.connected && <span className="text-zinc-600">(not connected)</span>}
              {g.last_error && <span className="text-rose-400" title={g.last_error}>· error</span>}
              {g.ghl_bot?.state === "on" && <span className="text-rose-400">· GHL bot on</span>}
            </label>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-zinc-300">
          <input type="checkbox" checked={data.settings.skip_assigned} disabled={busy}
            onChange={() => save({ ...data.settings, skip_assigned: !data.settings.skip_assigned })} data-testid="assistant-skip-assigned" />
          Don&apos;t answer conversations assigned to a person in GoHighLevel
        </label>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={checkBots} disabled={busy}>
          <RefreshCw className="h-3 w-3 mr-1" /> Check GoHighLevel bots now
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="assistant-stats">
        <Stat label="Conversations today" value={t.conversations} />
        <Stat label={data.mode === "dry" ? "Replies (not sent)" : "Replies sent"} value={t.replies} />
        <Stat label="Call times" value={t.call_times} />
        <Stat label="Call now" value={t.call_now} />
        <Stat label="Handovers" value={t.handovers} />
        <Stat label="Not interested" value={t.not_interested} />
      </div>

      <ABCard />

      {handovers.length > 0 && (
        <div className="card-surface p-4 space-y-2" data-testid="assistant-handovers">
          <h3 className="text-sm font-semibold">Needs a person ({handovers.length})</h3>
          {handovers.map((c) => (
            <div key={c.conversation_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-500/20 px-3 py-2 text-xs">
              <div>
                <span className="font-medium">{c.contact_name || "Lead"}</span>
                <span className="text-zinc-500"> · {shortGym(c.gym)} · {CHANNEL[c.channel] || c.channel} · {ago(c.state_at)}</span>
                <div className="text-amber-200/90">{c.state_reason}</div>
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => convAction(nurtureAgentEnable, c.conversation_id, "The assistant will reply again")}>Re-enable</Button>
            </div>
          ))}
        </div>
      )}

      <div className="card-surface p-4 space-y-2" data-testid="assistant-feed">
        <h3 className="text-sm font-semibold">Recent replies</h3>
        {data.feed.length === 0 && <div className="text-xs text-zinc-500">Nothing yet.</div>}
        {data.feed.map((r) => (
          <div key={r.id} className="rounded-md border border-[#222] px-3 py-2 text-xs space-y-1">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <span className="font-medium">{r.lead_name || "Lead"}</span>
                <span className="text-zinc-500"> · {shortGym(r.gym)} · {CHANNEL[r.channel] || r.channel} · {ago(r.at)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                {r.intent && <span className="rounded bg-violet-500/15 px-1.5 text-[10px] text-violet-300">{INTENT[r.intent] || r.intent}</span>}
                <span className={`rounded px-1.5 text-[10px] ${r.handover ? "bg-amber-500/15 text-amber-300" : r.kind === "error" ? "bg-rose-500/15 text-rose-300" : "bg-zinc-500/15 text-zinc-300"}`}>
                  {r.kind === "error" ? "Error" : ACTION[r.action] || r.action}
                </span>
                {r.dry && <span className="rounded bg-amber-500/10 px-1.5 text-[10px] text-amber-300">dry run</span>}
              </div>
            </div>
            <div className="text-zinc-400 whitespace-pre-line">Lead: {r.inbound}</div>
            {r.reply && <div className="text-zinc-100 whitespace-pre-line">Reply: {r.reply}{r.send_state === "error" ? <span className="text-rose-400"> (not sent: {r.send_error})</span> : null}</div>}
            {(r.handover || r.kind === "error") && (r.reason || r.detail) && <div className="text-amber-300/80">Why: {r.reason || r.detail}</div>}
            {r.callback_label && <div className="text-sky-300">Callback: {r.callback_label}{r.outside_hours ? " (moved into calling hours)" : ""}</div>}
            {r.kind !== "error" && !r.handover && r.action !== "stopped" && r.action !== "not_interested" && (
              <button className="text-[11px] text-zinc-400 underline" onClick={() => convAction(nurtureAgentTakeOver, r.conversation_id, "The assistant won't reply in this conversation")}>
                Take over
              </button>
            )}
          </div>
        ))}
      </div>

      {others.length > 0 && (
        <div className="card-surface p-4 space-y-2">
          <h3 className="text-sm font-semibold">Assistant stopped</h3>
          {others.map((c) => (
            <div key={c.conversation_id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <span>{c.contact_name || "Lead"} <span className="text-zinc-500">· {shortGym(c.gym)} · {c.state_reason}</span></span>
              <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => convAction(nurtureAgentEnable, c.conversation_id, "The assistant will reply again")}>Re-enable</Button>
            </div>
          ))}
        </div>
      )}

      {data.skips.length > 0 && (
        <div className="card-surface p-4 space-y-1">
          <h3 className="text-sm font-semibold">Not answered (latest)</h3>
          {data.skips.map((r) => (
            <div key={r.id} className="text-[11px] text-zinc-400">
              {ago(r.at)} · {r.lead_name || "Lead"} · {shortGym(r.gym)} · <span className="text-zinc-300">{data.skip_labels[r.reason] || r.reason}</span>{r.detail ? ` (${r.detail})` : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
